import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { gsap } from "gsap";
import { cursosAPI, progressoAPI } from '../services/api';
import VideoPlayer from '../components/VideoPlayer';
import { AppSidebar } from '../components/AppSidebar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Play, 
  Clock, 
  Users, 
  Award, 
  ChevronLeft, 
  BookOpen, 
  CheckCircle, 
  PlayCircle,
  Plus,
  Star,
  ArrowLeft,
  Building,
  Tag,
  Layers
} from 'lucide-react';

const API_BASE_URL = 'http://localhost:5000';

function DetalheCurso() {
  const { id } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [curso, setCurso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [progressoCurso, setProgressoCurso] = useState(null);
  const [estruturaCurso, setEstruturaCurso] = useState(null);
  const usuarioId = 'user1'; // Por enquanto, usuário fixo

  useEffect(() => {
    carregarCurso();
  }, [id]);

  // Atualizar progresso periodicamente quando estiver na página
  useEffect(() => {
    if (!curso || !progressoCurso) return;

    const interval = setInterval(() => {
      atualizarProgressoTempoReal();
    }, 30000); // Atualizar a cada 30 segundos

    return () => clearInterval(interval);
  }, [curso, progressoCurso]);

  // Animações GSAP
  useEffect(() => {
    if (loading || !curso) return;

    const ctx = gsap.context(() => {
      // Verificar se elementos existem antes de animar
      const fadeInElements = containerRef.current?.querySelectorAll(".fade-in")
      if (fadeInElements && fadeInElements.length > 0) {
        gsap.fromTo(
          fadeInElements,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.1,
            ease: "power2.out",
            delay: 0.2,
          },
        );
      }

      // Animação dos cards
      const cardElements = containerRef.current?.querySelectorAll(".card-animate")
      if (cardElements && cardElements.length > 0) {
        gsap.fromTo(
          cardElements,
          { opacity: 0, scale: 0.95 },
          {
            opacity: 1,
            scale: 1,
            duration: 0.6,
            stagger: 0.1,
            ease: "power2.out",
            delay: 0.4,
          },
        );
      }
    }, containerRef);

    return () => ctx.revert();
  }, [loading, curso]);

  const carregarCurso = async () => {
    try {
      setLoading(true);
      const response = await cursosAPI.obter(id);
      setCurso(response.data);
      
      // Carregar progresso do curso
      await carregarProgressoCurso();
    } catch (err) {
      setError('Erro ao carregar curso');
      console.error('Erro:', err);
    } finally {
      setLoading(false);
    }
  };

  const carregarProgressoCurso = async () => {
    try {
      const [progressoResponse, estruturaResponse] = await Promise.all([
        progressoAPI.obterProgressoCurso(id, usuarioId),
        progressoAPI.obterEstruturaCursoComProgresso(id, usuarioId)
      ]);
      
      setProgressoCurso(progressoResponse.data);
      setEstruturaCurso(estruturaResponse.data);
    } catch (err) {
      console.error('Erro ao carregar progresso do curso:', err);
    }
  };

  const atualizarProgressoTempoReal = async () => {
    // Função para atualizar progresso em tempo real sem recarregar a página
    try {
      const [progressoResponse, estruturaResponse] = await Promise.all([
        progressoAPI.obterProgressoCurso(id, usuarioId),
        progressoAPI.obterEstruturaCursoComProgresso(id, usuarioId)
      ]);
      
      setProgressoCurso(progressoResponse.data);
      setEstruturaCurso(estruturaResponse.data);
    } catch (err) {
      console.error('Erro ao atualizar progresso:', err);
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <AppSidebar />
        <div className="ml-64 min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 border-4 border-slate-700 border-t-red-600 rounded-full animate-spin mx-auto"></div>
            <p className="text-white text-lg">Carregando curso...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !curso) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <AppSidebar />
        <div className="ml-64 min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mb-4">
              <BookOpen className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">Curso não encontrado</h2>
            <p className="text-slate-400">O curso que você está procurando não existe ou foi removido.</p>
            <Button onClick={() => navigate('/')} className="bg-red-600 hover:bg-red-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Início
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const progressoGeral = progressoCurso?.progresso_geral || { porcentagem: 0, videos_concluidos: 0, total_videos: 0 };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <AppSidebar />
      <div ref={containerRef} className="ml-64 min-h-screen">
        {/* Header/Breadcrumb */}
        <div className="fade-in border-b border-slate-800/50 bg-slate-950/50 backdrop-blur-xl sticky top-0 z-40">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/')}
                  className="text-slate-400 hover:text-white hover:bg-slate-800/50"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
                <div className="text-slate-400">/</div>
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-4 h-4 text-red-400" />
                  <span className="text-white font-medium">{curso.titulo}</span>
                </div>
              </div>
              
              {progressoCurso?.progresso_geral?.curso_concluido && (
                <Badge className="bg-green-600/20 text-green-400 border-green-600/30">
                  <Award className="w-3 h-3 mr-1" />
                  Curso Concluído
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 py-8">
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
            {/* Conteúdo Principal */}
            <div className="xl:col-span-3 space-y-6">
              {/* Informações do Curso */}
              <div className="fade-in card-animate bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-xl overflow-hidden">
                {/* Capa do Curso */}
                <div className="relative h-64 md:h-80">
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: curso.capa 
                        ? `url(${API_BASE_URL}/uploads/${curso.capa})` 
                        : `linear-gradient(135deg, #1e293b 0%, #0f172a 50%)`,
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
                  
                  {/* Informações sobre a capa */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      {curso.categoria_nome && (
                        <Badge className="bg-red-600/20 text-red-400 border-red-600/30">
                          <Tag className="w-3 h-3 mr-1" />
                          {curso.categoria_nome}
                        </Badge>
                      )}
                      {curso.empresa_nome && (
                        <Badge variant="outline" className="border-slate-600 text-slate-300">
                          <Building className="w-3 h-3 mr-1" />
                          {curso.empresa_nome}
                        </Badge>
                      )}
                    </div>
                    
                    <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
                      {curso.titulo}
                    </h1>
                    
                    <div className="flex items-center gap-4 text-slate-300">
                      <div className="flex text-yellow-400">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                      <span>4.9</span>
                      <span>•</span>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{curso.total_alunos || 0} alunos</span>
                      </div>
                      {curso.duracao && (
                        <>
                          <span>•</span>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{curso.duracao}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Conteúdo do Curso */}
                <div className="p-6">
                  {/* Progresso do Curso */}
                  {progressoCurso?.progresso_geral && (
                    <div className="mb-6 p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-white">Seu Progresso</h3>
                        <span className="text-slate-300 text-sm">
                          {progressoGeral.videos_concluidos} de {progressoGeral.total_videos} vídeos
                        </span>
                      </div>
                      <Progress 
                        value={progressoGeral.porcentagem} 
                        className="h-3 mb-2" 
                      />
                      <p className="text-sm text-slate-400">
                        {progressoGeral.porcentagem}% concluído
                      </p>
                    </div>
                  )}
                  
                  {/* Descrição */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-white mb-3">Sobre este curso</h3>
                    <p className="text-slate-300 leading-relaxed">
                      {curso.descricao || 'Este é um curso completo e atualizado para você dominar esta tecnologia com as melhores práticas do mercado.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Botão Assistir Curso */}
              <div className="fade-in card-animate bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-xl p-6">
                <div className="text-center">
                  <div className="w-20 h-20 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <PlayCircle className="w-10 h-10 text-red-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Comece a aprender agora!</h3>
                  <p className="text-slate-400 mb-6">
                    Este curso possui {curso.videos?.length || 0} vídeos organizados em uma sequência de aprendizado
                  </p>
                  <Button 
                    onClick={() => navigate(`/assistir-curso/${id}`)}
                    className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 text-lg"
                    size="lg"
                  >
                    <PlayCircle className="w-5 h-5 mr-2" />
                    Assistir Curso
                  </Button>
                </div>
              </div>
            </div>

            {/* Sidebar de Informações */}
            <div className="xl:col-span-1">
              <div className="fade-in card-animate bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-xl overflow-hidden sticky top-24">
                <div className="p-4 border-b border-slate-800/50">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-red-400" />
                    Sobre o Curso
                  </h3>
                </div>

                <div className="p-4 space-y-4">
                  {/* Estatísticas */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-slate-800/30 rounded-lg">
                      <div className="text-2xl font-bold text-white">{curso.videos?.length || 0}</div>
                      <div className="text-xs text-slate-400">Vídeos</div>
                    </div>
                    <div className="text-center p-3 bg-slate-800/30 rounded-lg">
                      <div className="text-2xl font-bold text-white">{curso.total_alunos || 0}</div>
                      <div className="text-xs text-slate-400">Alunos</div>
                    </div>
                  </div>

                  {/* Instrutor */}
                  {curso.instrutor_nome && (
                    <div className="p-3 bg-slate-800/30 rounded-lg">
                      <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                        <Users className="w-4 h-4 text-red-400" />
                        Instrutor
                      </h4>
                      <div className="flex items-center gap-3">
                        {curso.instrutor_foto && (
                          <img 
                            src={`${API_BASE_URL}/uploads/${curso.instrutor_foto}`}
                            alt={curso.instrutor_nome}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        )}
                        <div>
                          <p className="text-sm font-medium text-white">{curso.instrutor_nome}</p>
                          {curso.instrutor_bio && (
                            <p className="text-xs text-slate-400 line-clamp-2">{curso.instrutor_bio}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Grade Curricular */}
                  <div className="p-3 bg-slate-800/30 rounded-lg">
                    <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Layers className="w-4 h-4 text-red-400" />
                      Grade Curricular
                    </h4>
                    
                    {estruturaCurso ? (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {/* Módulos */}
                        {estruturaCurso.estrutura.modulos.map((modulo) => (
                          <div key={modulo.id} className="border border-slate-700/50 rounded-lg overflow-hidden">
                            <div className="p-2 bg-slate-700/30 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-5 h-5 bg-blue-600/20 rounded flex items-center justify-center">
                                  <span className="text-xs font-bold text-blue-400">{modulo.ordem}</span>
                                </div>
                                <span className="text-xs font-semibold text-white truncate">{modulo.nome}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-400">{modulo.videos_concluidos}/{modulo.total_videos}</span>
                                <div className="w-8 h-1 bg-slate-600 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-green-500 transition-all duration-300"
                                    style={{ width: `${modulo.porcentagem || 0}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                            
                            {/* Vídeos do módulo */}
                            <div className="p-2 space-y-1">
                              {modulo.videos.slice(0, 3).map((video, videoIndex) => (
                                <div key={video.id} className="flex items-center gap-2 text-xs">
                                  <div className={`w-4 h-4 rounded-full flex items-center justify-center text-xs ${
                                    video.concluido 
                                      ? 'bg-green-600 text-white' 
                                      : 'bg-slate-600 text-slate-300'
                                  }`}>
                                    {video.concluido ? (
                                      <CheckCircle className="w-3 h-3" />
                                    ) : (
                                      <span>{video.ordem}</span>
                                    )}
                                  </div>
                                  <span className={`truncate flex-1 ${
                                    video.concluido ? 'text-green-400' : 'text-slate-300'
                                  }`}>
                                    {video.titulo}
                                  </span>
                                  {video.porcentagem_assistida > 0 && !video.concluido && (
                                    <span className="text-xs text-blue-400">
                                      {video.porcentagem_assistida}%
                                    </span>
                                  )}
                                </div>
                              ))}
                              {modulo.videos.length > 3 && (
                                <p className="text-xs text-slate-400 text-center mt-1">
                                  +{modulo.videos.length - 3} mais vídeos
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                        
                        {/* Vídeos sem módulo (legado) */}
                        {estruturaCurso.estrutura.videos_sem_modulo.length > 0 && (
                          <div className="border border-slate-700/50 rounded-lg overflow-hidden">
                            <div className="p-2 bg-slate-700/30">
                              <span className="text-xs font-semibold text-white">Vídeos Avulsos</span>
                            </div>
                            <div className="p-2 space-y-1">
                              {estruturaCurso.estrutura.videos_sem_modulo.slice(0, 5).map((video, index) => (
                                <div key={video.id} className="flex items-center gap-2 text-xs">
                                  <div className={`w-4 h-4 rounded-full flex items-center justify-center text-xs ${
                                    video.concluido 
                                      ? 'bg-green-600 text-white' 
                                      : 'bg-slate-600 text-slate-300'
                                  }`}>
                                    {video.concluido ? (
                                      <CheckCircle className="w-3 h-3" />
                                    ) : (
                                      <span>{video.ordem}</span>
                                    )}
                                  </div>
                                  <span className={`truncate flex-1 ${
                                    video.concluido ? 'text-green-400' : 'text-slate-300'
                                  }`}>
                                    {video.titulo}
                                  </span>
                                  {video.porcentagem_assistida > 0 && !video.concluido && (
                                    <span className="text-xs text-blue-400">
                                      {video.porcentagem_assistida}%
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : curso.videos && curso.videos.length > 0 ? (
                      <div className="space-y-2">
                        {curso.videos.slice(0, 5).map((video, index) => (
                          <div key={video.id} className="flex items-center gap-2 text-xs">
                            <div className="w-5 h-5 bg-slate-700 rounded-full flex items-center justify-center text-xs font-semibold text-slate-300">
                              {index + 1}
                            </div>
                            <span className="text-slate-300 truncate">{video.titulo}</span>
                          </div>
                        ))}
                        {curso.videos.length > 5 && (
                          <p className="text-xs text-slate-400 text-center mt-2">
                            +{curso.videos.length - 5} mais vídeos
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400">Nenhum conteúdo disponível</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DetalheCurso; 